import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Box from '@material-ui/core/Box';
import capitalize from 'lodash/capitalize';
import Grid from '@material-ui/core/Grid';
import ConfirmationDialog from 'components/legacy/ConfirmationDialog';
import LWTypography from 'components/legacy/LWTypography';
import LWSelect from 'components/legacy/LWSelect';
import useProjectAdd from 'modules/queries/project/useAdd';
import toast from '@liquidweb-dev/undercurrent/toast';
import useProjectCategories from 'modules/queries/project/useCategories';
import useProjectAvailableAssets from 'modules/queries/project/useAvailableAssets';
import { selectors } from 'modules/projectAddDialog/selectors';
import actions from 'modules/projectAddDialog/actions';
import ClusterVerbiageContent from './ClusterVerbiageContent';

const ProjectAddDialog = () => {
	const dispatch = useDispatch();

	const isOpen = useSelector(selectors.isOpen);
	const dialogProps = useSelector(selectors.dialogProps);
	const { projectId, projectName } = dialogProps;

	const [category, setCategory] = useState('');
	const [asset, setAsset] = useState({ uniq_id: '', domain: '', type: '' });
	const [assetCurrentProject, setAssetCurrentProject] = useState({
		uniqId: '',
		name: '',
	});
	const [showClusterVerbiage, setShowClusterVerbiage] = useState(false);

	const { mutate: addToProject } = useProjectAdd();

	const { data: categoriesData, isLoading: isLoadingCategoriesData } =
		useProjectCategories({ enabled: isOpen });

	const isEnabledAvailableAssets = Boolean(category) && Boolean(projectId);

	const { data: availableAssetsData, isLoading: isLoadingAvailableAssets } =
		useProjectAvailableAssets(
			{ category, id: projectId },
			{ enabled: isEnabledAvailableAssets },
		);

	const toggleDialog = (value) => dispatch(actions.toggleDialog(value));

	// reset select fields on dialog open
	useEffect(() => {
		if (!isOpen) return;

		setCategory('');
		setAsset({ uniq_id: '', domain: '', type: '' });
		setAssetCurrentProject({ uniqId: '', name: '' });
		setShowClusterVerbiage(false);
	}, [isOpen]);

	const changeCategory = (value) => setCategory(value);

	const changeAsset = (value) => {
		const assetData = availableAssetsData.assets.find(
			(a) => a.uniq_id === value,
		);
		setAsset(assetData);
		setAssetCurrentProject({
			uniqId: assetData.project_id,
			name: assetData.project_name,
		});
	};

	const thereIsMore = useMemo(
		() => category === 'servers' && asset.clusterable && !showClusterVerbiage,
		[category, asset, showClusterVerbiage],
	);

	const handleClose = () => {
		if (!thereIsMore) {
			toggleDialog(false);
		}
	};

	const handleConfirm = () => {
		if (thereIsMore) {
			setShowClusterVerbiage(true);
		} else {
			addToProject(
				{
					assets: [asset.uniq_id],
					id: projectId,
					cluster: 1, // hard coded per NEWMAN-1144
				},
				{
					onSuccess: () => {
						toast.success(
							`Successfully added ${asset.domain} to ${projectName}`,
						);
					},
				},
			);
		}
	};

	const errorTitle = 'That Was Unexpected';

	const confirmText = useMemo(() => {
		if (showClusterVerbiage) return 'Confirm';

		return assetCurrentProject.uniqId
			? 'Transfer to this Project'
			: 'Add to Project';
	}, [showClusterVerbiage, assetCurrentProject]);

	const title = useMemo(() => {
		if (showClusterVerbiage) {
			switch (asset.potential_role) {
				case 'client':
					return 'File Replication Enabled';
				case 'primary':
				case 'read-only':
					return `Database Clustering for ${asset.domain}`;
				default:
					return errorTitle;
			}
		}
		return 'Add Asset to Project';
	}, [showClusterVerbiage, asset, errorTitle]);

	const confirmable = Boolean(
		errorTitle !== title && category && asset && asset.uniq_id,
	);

	return (
		<ConfirmationDialog
			confirmProps={{ disabled: !confirmable }}
			open={isOpen}
			onConfirm={handleConfirm}
			onCancel={() => toggleDialog(false)}
			onClose={handleClose}
			title={title}
			confirmText={confirmText}
		>
			{showClusterVerbiage ? (
				<ClusterVerbiageContent
					potentialRole={asset.potential_role}
					uniqId={asset.uniq_id}
				/>
			) : (
				<Box pt={2} px={1} width={['100%', '100%', 552]}>
					<Grid container direction="column" spacing={3}>
						<Grid item>
							<LWTypography>
								Select the asset you would like to add to {projectName}.
							</LWTypography>
						</Grid>
						<Grid item>
							<LWSelect
								label="Category"
								isLoading={isLoadingCategoriesData}
								value={category}
								onChange={changeCategory}
								options={
									categoriesData?.categories?.map((categoryName) => ({
										label: capitalize(categoryName),
										value: categoryName,
									})) || []
								}
							/>
						</Grid>
						<Grid item>
							<LWSelect
								label={
									availableAssetsData?.assets?.length > 0 && category
										? `My ${category}`
										: ''
								}
								isLoading={isEnabledAvailableAssets && isLoadingAvailableAssets}
								disabled={!availableAssetsData?.assets?.length > 0}
								value={asset.uniq_id}
								onChange={changeAsset}
								options={
									availableAssetsData?.assets?.map((assetData) => ({
										value: assetData.uniq_id,
										label: assetData.domain,
									})) || []
								}
								error={Boolean(assetCurrentProject.uniqId)}
								helperText={
									assetCurrentProject.uniqId
										? `${asset.domain} already belongs to ${assetCurrentProject.name}.`
										: ''
								}
							/>
						</Grid>
					</Grid>
				</Box>
			)}
		</ConfirmationDialog>
	);
};

export default ProjectAddDialog;
